<script>
    import { afterUpdate } from 'svelte';
    import clickOutside from 'svelte-outside-click';
    import { animateScroll } from 'svelte-scrollto-element';
    import { German } from 'flatpickr/dist/l10n/de';
    import { english as English } from 'flatpickr/dist/l10n/default';
    import 'flatpickr/dist/flatpickr.css';
    import Dropdown from '../../../_wrappers/Dropdown.svelte';
    import Emoji from '../../../Emoji.svelte';
    import StatusIcon from '../StatusIcon.svelte';
    import BottomSheet from '../../../_wrappers/BottomSheet.svelte';
    import DatePickerInputContent from './DatePickerInputContent.svelte';
    import DatePickerInputActionButtons from './DatePickerInputActionButtons.svelte';
    import DeviceDetector from 'svelte-device-detector';
    import clsx from 'clsx';
    import uniqueId from 'lodash/uniqueId';

    // Utils
    import { isDayDisabled, isDayFocusable } from '../../helper';
    import { getBorderRadius } from '$lib/global/helper/border-radius';

    export let placeholder = '';
    export let customOptions = {
        language: '',
        pastDatesDisabled: false,
        futureDatesDisabled: false,
        weekendsDisabled: false,
    };
    export let showEmoji = false;
    export let emoji = '';
    export let required = false;
    export let invalid = false;
    export let pristine = true;
    export let touched = false;
    export let isFirst = false;
    export let isLast = false;
    export let finalFormInput = {};
    export let errorMessage = '';

    let open = false;
    let focused = false;

    let showError = invalid && touched;

    let wrapperClasses;
    let inputWrapperClasses;
    let inputClasses;
    let chevronSvgClasses;

    let dateSelected, lastConfirmedDate, formattedDate;
    let inputRef, confirmBtnRef, clearBtnRef;

    const nextMonthArrowId = uniqueId('next-month-arrow');
    const prevMonthArrowId = uniqueId('prev-month-arrow');
    const dropdownId = uniqueId('dropdown');

    const translations = {
        de: {
            clear: 'Löschen',
            confirm: 'Speichern',
            l10n: German,
        },
        en: {
            clear: 'Clear',
            confirm: 'Confirm',
            l10n: {
                ...English,
                weekdays: {
                    ...English.weekdays,
                    shorthand: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
                },
            },
        },
    };

    const now = new Date().setHours(0, 0, 0, 0);

    // Default language to 'de'
    const language = customOptions.language || 'de';

    afterUpdate(() => {
        showError = !lastConfirmedDate && required && touched;
    });

    const setConfirmButtonRef = (ref) => {
        confirmBtnRef = ref;
    };

    const setClearButtonRef = (ref) => {
        clearBtnRef = ref;
    };

    const handleInputClick = () => {
        open ? handleCalendarClose() : handleCalendarOpen();
    };

    const handleClickOutside = () => {
        if (!open) return;

        handleCalendarClose();
    };

    const handleGlobalKeyDown = (event) => {
        const travelBack = event.shiftKey && event.key === 'Tab';

        if (travelBack) {
            open && handleCalendarClose();
        }

        // Handle [space] or [enter] keydown
        if (event.key === ' ' || event.key === 'Enter') {
            event.preventDefault();
            open ? handleCalendarClose() : handleCalendarOpen();
        }
    };

    const handleGlobalBlur = () => {
        if (!open) {
            focused = false;
        }

        finalFormInput.onBlur();
    };

    // Handle Shift + Tab from Calendar Days
    const focusBackFromCalendar = () => {
        const nextMonthArrow = document.getElementById(nextMonthArrowId);
        const prevMonthArrow = document.getElementById(prevMonthArrowId);

        if (
            nextMonthArrow &&
            nextMonthArrow.parentElement.className.indexOf('flatpickr-disabled') < 0
        ) {
            nextMonthArrow.focus();
        } else if (
            prevMonthArrow &&
            prevMonthArrow.parentElement.className.indexOf('flatpickr-disabled') < 0
        ) {
            prevMonthArrow.focus();
        } else {
            inputRef.focus();
        }
    };

    const handleDayKeyDown = (event) => {
        const travelBack = event.shiftKey && event.key === 'Tab';
        const travelForward = event.key === 'Tab';
        const enterPressed = event.key === 'Enter';
        const spacePressed = event.key === ' ';

        if (travelBack) {
            if (!isDayDisabled(event.target.previousElementSibling)) {
                event.target.previousElementSibling?.focus();
            } else {
                focusBackFromCalendar();
            }
        } else if (travelForward) {
            if (!isDayFocusable(event.target.nextElementSibling)) {
                event.preventDefault();
                dateSelected && clearBtnRef.focus();
            }
        }

        // Handle select date
        if (enterPressed || spacePressed) {
            event.preventDefault();
            dateSelected = event.target.dateObj;
            setTimeout(() => {
                confirmBtnRef.focus();
            });
        }
    };

    const handleEscClick = (event) => {
        if (event.key === 'Escape') {
            handleCalendarClose();
        }
    };

    const handleFormattedDate = (dateSelected) => {
        formattedDate = dateSelected && new Date(dateSelected).toLocaleDateString();
    };

    const handleClear = (e) => {
        e.preventDefault();

        dateSelected = '';
    };

    const handleConfirm = (e) => {
        e.preventDefault();

        if (dateSelected) {
            const dateSelectedUTCTimestamp = Date.UTC(
                dateSelected.getFullYear(),
                dateSelected.getMonth(),
                dateSelected.getDate(),
            );
            const dateSelectedUTC = new Date(dateSelectedUTCTimestamp);
            const dateSelectedIso = dateSelectedUTC.toISOString();

            lastConfirmedDate = dateSelected;
            // Update global input
            inputRef.value = dateSelectedIso;
            finalFormInput.onChange(dateSelectedIso);
            handleFormattedDate(dateSelected);
            handleCalendarClose();
        }
    };

    const handleChange = (value) => {
        dateSelected = value?.detail?.[0]?.[0];
    };

    const handleCalendarOpen = () => {
        open = true;
        focused = true;
        scrollIntoView();
        inputRef?.focus();
    };

    const handleCalendarClose = () => {
        // Default to last confirmed date
        dateSelected = lastConfirmedDate;
        handleFormattedDate(lastConfirmedDate);

        open = false;
        focused = false;
        touched = true;
        inputRef?.focus();
    };

    const scrollIntoView = () => {
        const dropdown = document.querySelector(`#${dropdownId}`);
        if (dropdown) {
            // Offset the height of the input field
            animateScroll?.scrollTo({ element: dropdown, offset: -64 });
        }
    };

    const options = {
        enableTime: false,
        inline: true,
        static: true,
        locale: {
            ...translations[language].l10n,
            firstDayOfWeek: 1,
        },
        monthSelectorType: 'static',
        closeOnSelect: false,
        minDate: customOptions.pastDatesDisabled ? now : undefined,
        maxDate: customOptions.futureDatesDisabled ? now : undefined,
        nextArrow: `<div id="${nextMonthArrowId}" class="month-picker-arrow" tabindex="0">
                        <svg class='rotate-90 ml-0.5' width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M6.55806 0.433058C6.80214 0.188981 7.19786 0.188981 7.44194 0.433058L13.6919 6.68306C13.936 6.92714 13.936 7.32286 13.6919 7.56694C13.4479 7.81102 13.0521 7.81102 12.8081 7.56694L7 1.75888L1.19194 7.56694C0.947864 7.81102 0.552136 7.81102 0.308058 7.56694C0.0639806 7.32286 0.0639806 6.92714 0.308058 6.68306L6.55806 0.433058Z"
                                fill="#6B7280"
                            />
                        </svg>
                    </div>`,
        prevArrow: `<div id="${prevMonthArrowId}" class="month-picker-arrow" tabindex="0">
                        <svg class='-rotate-90 mr-0.5' width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M6.55806 0.433058C6.80214 0.188981 7.19786 0.188981 7.44194 0.433058L13.6919 6.68306C13.936 6.92714 13.936 7.32286 13.6919 7.56694C13.4479 7.81102 13.0521 7.81102 12.8081 7.56694L7 1.75888L1.19194 7.56694C0.947864 7.81102 0.552136 7.81102 0.308058 7.56694C0.0639806 7.32286 0.0639806 6.92714 0.308058 6.68306L6.55806 0.433058Z"
                                fill="#6B7280"
                            />
                        </svg>
                    </div>`,
        onDayCreate: (_a, _b, _c, data) => {
            if (!isDayDisabled(data)) {
                // Make days focusable + keyboard controls + add 0's
                data.tabIndex = 0;
                data.onkeydown = handleDayKeyDown;
                data.innerHTML =
                    "<div class='flatpickr-day-box'>" +
                    (+data.innerHTML < 10 ? '0' : '') +
                    data.innerHTML +
                    '</div>';
            } else if (data.className.indexOf('flatpickr-disabled') > -1) {
                data.innerHTML = (+data.innerHTML < 10 ? '0' : '') + data.innerHTML;
                data.tabIndex = '-1';
            }
        },
        disable: [
            (date) => {
                // Disable weekends if needed
                if (customOptions.weekendsDisabled) {
                    return date.getDay() === 0 || date.getDay() === 6;
                }
            },
        ],
    };

    $: wrapperClasses = clsx('wrapper relative', {
        invalid: invalid || showError,
    });

    const borderRadius = getBorderRadius('fieldsWrapper');

    $: inputWrapperClasses = clsx(
        borderRadius,
        'input-wrapper flex relative box-border relative overflow-hidden transition-all',
        {
            ['rounded-b-none']: !isLast,
            ['rounded-t-none']: !isFirst,
            ['h-[90px]']: showError,
            ['border-b']: isFirst && isLast,
        },
    );

    $: inputClasses = clsx(
        borderRadius,
        'input flex flex-row cursor-pointer box-border w-full pl-6 pr-10 border-0 border-l border-r border-t border-black/10 mb-0 font-ui text-base placeholder:truncate transition-all font-light caret-transparent focus:outline-none disabled:opacity-100',
        {
            ['bg-gray-50 outline-none']: focused,
            ['bg-white']: !focused,
            ['pr-32']: !required && !dateSelected,
            ['rounded-b-none']: !isLast,
            ['rounded-t-none']: !isFirst,
            ['border-b']: (((invalid || showError) && isFirst) || !isFirst) && isLast,
            ['h-[90px] pb-[26px]']: showError,
            ['h-[64px]']: !showError,
            ['pl-[70px]']: showEmoji,
            ['pr-24']: !required && pristine,
        },
    );

    $: chevronSvgClasses = clsx(
        'w-4 h-4 absolute top-[26px] text-gray-500 transition-all duration-[400ms] ease-[cubic-bezier(0.16,1,0.3,1)] transform pointer-events-none cursor-pointer',
        {
            '-rotate-180': !open,
            'rotate-0': open,
            'right-16': showError,
            'right-6': !showError,
        },
    );
</script>

<div class={wrapperClasses} use:clickOutside={handleClickOutside} on:keydown={handleEscClick}>
    <div class={inputWrapperClasses}>
        {#if showEmoji}
            <u class="absolute left-6 mt-6 flex h-[18px] select-none items-center no-underline">
                <Emoji {emoji} size="24px" />
            </u>
        {/if}

        {#if !required && !invalid && pristine}
            <u
                class="pointer-events-none absolute right-14 mt-6 text-xs font-light text-gray-500 no-underline"
            >
                (optional)
            </u>
        {/if}

        {#if showError}
            <StatusIcon showCheckmark={false} showSuccess={false} {showError} />
        {/if}

        <!-- Click catcher -->
        <div
            class="absolute bottom-0 left-0 right-0 top-0 z-10 cursor-pointer"
            on:click={handleInputClick}
        />

        <input
            readonly
            class="absolute -z-10 opacity-0"
            name={finalFormInput.name}
            bind:value={formattedDate}
            on:focus={() => {
                focused = true;
                finalFormInput.onFocus();
            }}
            on:blur={handleGlobalBlur}
            on:keydown={handleGlobalKeyDown}
            bind:this={inputRef}
        />

        <input
            disabled
            class={inputClasses}
            {placeholder}
            tabindex="-1"
            autocomplete="off"
            bind:value={formattedDate}
        />

        <svg
            class={chevronSvgClasses}
            width="14"
            height="8"
            viewBox="0 0 14 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M6.55806 0.433058C6.80214 0.188981 7.19786 0.188981 7.44194 0.433058L13.6919 6.68306C13.936 6.92714 13.936 7.32286 13.6919 7.56694C13.4479 7.81102 13.0521 7.81102 12.8081 7.56694L7 1.75888L1.19194 7.56694C0.947864 7.81102 0.552136 7.81102 0.308058 7.56694C0.0639806 7.32286 0.0639806 6.92714 0.308058 6.68306L6.55806 0.433058Z"
                fill="#6B7280"
            />
        </svg>

        {#if showError}
            <div
                class="absolute left-[70px] top-14 text-xs font-normal leading-tight text-[#ef444a]"
            >
                {errorMessage}
            </div>
        {/if}
    </div>

    <DeviceDetector showInDevice="mobile">
        <BottomSheet
            {open}
            title={placeholder}
            handleClose={handleCalendarClose}
            actionButtonsComponent={DatePickerInputActionButtons}
            actionButtonsProps={{
                disabled: !dateSelected,
                setConfirmButtonRef,
                setClearButtonRef,
                btnClearLabel: translations[language].clear,
                btnConfirmLabel: translations[language].confirm,
                handleConfirm,
                handleClear,
                open,
            }}
        >
            <DatePickerInputContent
                {options}
                {dateSelected}
                {handleChange}
                {prevMonthArrowId}
                {nextMonthArrowId}
                {open}
            />
        </BottomSheet>
    </DeviceDetector>

    <DeviceDetector showInDevice={['desktop', 'tablet']}>
        <Dropdown id={dropdownId} {open} class="justify-end">
            <DatePickerInputContent
                {options}
                {dateSelected}
                {handleChange}
                {prevMonthArrowId}
                {nextMonthArrowId}
                {open}
            />
            <DatePickerInputActionButtons
                disabled={!dateSelected}
                {setConfirmButtonRef}
                {setClearButtonRef}
                btnClearLabel={translations[language].clear}
                btnConfirmLabel={translations[language].confirm}
                {handleConfirm}
                {handleClear}
                {open}
            />
        </Dropdown>
    </DeviceDetector>
</div>

<style lang="scss">
    @import './styles/flatpickr.styles.scss';

    .wrapper {
        .input-wrapper {
            box-shadow: 0 1px 1px 0 rgba(#000, 0.1);

            :global([placeholder]) {
                @apply truncate;
            }
        }
    }
</style>
